import React, { useState, useEffect, useCallback, useRef } from "react";
import { SearchParameters } from "../model/SearchParameters";
import type { CatalogCategory } from "../model/CatalogCategory";
import { resolveVirtualCategory } from "./resolveVirtualCategory";
import api from "./api";
import { API_URLS } from "../App";

interface SearchResult {
  results: Compound[];
  total_pages: number;
  total_results: number;
  current_page: number;
  page_size: number;
  filters_summary_result: SearchFilters;
}

interface SearchFilters {
  [key: string]: SearchFilterItem[] | string | null;
  memory: SearchFilterItem[];
  storage: SearchFilterItem[];
  cpu: SearchFilterItem[];
  gpu: SearchFilterItem[];
  skip_filter: string | null;
}

interface UseFetchDataResult {
  categories: CatalogCategory[];
  compounds: Compound[];
  totalPages: number;
  filters: SearchFilters | null;
}

export const useFetchData = (
  searchParams: SearchParameters,
  setLoading: (loading: LoadingScreenProps | null) => void,
  setError: (error: string | null) => void,
): UseFetchDataResult => {
  const lastResolvedKey = useRef<string>("");
  const [pcs, setPCs] = useState<Compound[]>([]);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState<SearchFilters | null>(null);

  const fetchPCs = useCallback(
    async (params: SearchParameters) => {
      setError(null);
      api
        .post(API_URLS.CATALOG + "/_search", params)
        .then((res) => {
          const responseData: SearchResult = res.data;
          setPCs(responseData.results);
          setTotalPages(responseData.total_pages);
          setFilters(responseData.filters_summary_result);
        })
        .catch((err) => {
          setError("Ocorreu um erro ao buscar os PCs: " + err.message);
          setPCs([]);
          setTotalPages(0);
          setFilters(null);
        })
        .finally(() => setLoading(null));
    },
    [setLoading, setError],
  );

  const fetchCategories = useCallback(async (): Promise<CatalogCategory[]> => {
    setError(null);
    try {
      const res = await api.get(API_URLS.CATALOG + "/metadata/categories");
      const categories = res.data as CatalogCategory[];
      setCategories(categories);
      return categories;
    } catch (err) {
      setError(
        "Ocorreu um erro ao buscar as categorias: " + (err as Error).message,
      );
      return [];
    } finally {
      setLoading(null);
    }
  }, [setLoading, setError]);

  const updateBrowserUrl = (params: SearchParameters) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (key === "skip_filter") {
        // Skip this filter from URL
        return;
      }
      if (key === "price_range" && Array.isArray(value)) {
        value.forEach((v, index) => {
          if (v !== null && v !== undefined) {
            queryParams.append(
              `${key}.min_price[${index}]`,
              String(v.min_price),
            );
            queryParams.append(
              `${key}.max_price[${index}]`,
              String(v.max_price),
            );
          }
        });
        return;
      }
      if (value !== null && value !== undefined && value !== "") {
        if (Array.isArray(value)) {
          value.forEach((v) => queryParams.append(key, String(v)));
        } else if (typeof value === "object") {
          // Handle object values if needed
          queryParams.set(key, JSON.stringify(value));
        } else {
          queryParams.set(key, String(value));
        }
      }
    });

    window.history.pushState(
      { ...history.state, searchParams: params },
      "",
      `${window.location.pathname}?${queryParams.toString()}`,
    );
  };

  useEffect(() => {
    if (categories.length === 0) {
      void fetchCategories();
    }
  }, [fetchCategories, categories.length]);

  useEffect(() => {
    if (!searchParams || categories.length === 0) return;

    const resolved = resolveVirtualCategory(categories, searchParams);
    if (resolved.isInvalid) {
      setPCs([]);
      setTotalPages(0);
      setFilters(null);
      setLoading(null);
      updateBrowserUrl(searchParams);
      return;
    }

    const key = JSON.stringify(resolved.params);
    if (key === lastResolvedKey.current) return;
    lastResolvedKey.current = key;
    void fetchPCs(resolved.params);
    updateBrowserUrl(searchParams);
  }, [searchParams, categories, fetchPCs, setLoading]);

  return {
    categories: categories.filter((category) => category.is_filterable),
    compounds: pcs,
    totalPages,
    filters,
  };
};
